import { BusinessObjectBase } from './base';
import { curriculumSchema } from '@schemas/business-objects/curriculum';
import type { Activity } from './activity';
import type { Prescription } from './prescription';
import type { Session } from './session';
import type { User } from './user';
import type { Id, SessionType } from '../base-types';
import type { ExportImportConfig } from '../interface-definitions/index';
import type { Resource } from './resource';
import type { TextBox } from './text-box';

export interface CurriculumData {
  readonly id: Id;
  readonly name?: string;
  readonly shortName?: string;
  readonly numberOfSessions?: number;
  readonly sessionType?: SessionType;
  readonly activities?: Activity[];
  readonly prescriptions?: Prescription[];
  readonly sessions?: Session[];
  readonly createdById: Id;
  readonly createdBy?: User;
  readonly publishedById: Id;
  readonly publishedBy?: User;
  readonly archivedById: Id;
  readonly archivedBy?: User;
  readonly locked?: boolean;
  readonly uuid?: string;
  readonly version?: number;
  readonly status?: string;
  readonly createdAt?: Date;
  readonly publishedAt?: Date;
  readonly archivedAt?: Date;
}

export interface Curriculum extends Readonly<CurriculumData> {}
type CurriculumCreateWithChildrenData = Partial<
  Omit<CurriculumData, 'activities'>
> & { activities?: Partial<Activity>[] };

export class Curriculum extends BusinessObjectBase {
  static readonly entityName = 'Curriculum' as const;
  static readonly plural = 'curricula';
  static schema = curriculumSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Curriculum[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Curriculum(data));
  }

  static async findOne(filter?: any): Promise<Curriculum | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Curriculum(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<Curriculum> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Curriculum(data);
  }

  static async create(data: Omit<CurriculumData, 'id'>): Promise<Curriculum> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Curriculum(result);
  }

  static async createMany(
    data: Omit<CurriculumData, 'id'>[],
  ): Promise<Curriculum[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Curriculum(item));
  }

  static async createWithChildren(
    data: CurriculumCreateWithChildrenData,
  ): Promise<Curriculum> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new Curriculum(result);
  }

  static async createManyWithChildren(
    data: CurriculumCreateWithChildrenData[],
  ): Promise<Curriculum[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Curriculum(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<CurriculumData>,
  ): Promise<Curriculum> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Curriculum(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async export(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Curriculum.plural}/${(this as any).id}/export`;
    return BusinessObjectBase.get<any>(url);
  }
  static async exportMany(body: any): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/export-many`;
    return this.post<any>(url, body);
  }
  async publish(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Curriculum.plural}/${(this as any).id}/publish`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async newVersion(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Curriculum.plural}/${(this as any).id}/new-version`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async latestVersion(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Curriculum.plural}/${(this as any).id}/latest-version`;
    return BusinessObjectBase.get<any>(url);
  }
  static async findLatestVersions(
    filter: any,
    publishedOnly?: boolean,
  ): Promise<any[]> {
    const url = `${this.baseUrl}/${this.plural}/find-latest-versions`;
    const queryParams = new URLSearchParams();
    if (filter !== undefined && filter !== null)
      queryParams.set('filter', JSON.stringify(filter));
    if (publishedOnly !== undefined && publishedOnly !== null)
      queryParams.set('publishedOnly', String(publishedOnly));
    const qs = queryParams.toString();
    const fullUrl = qs ? `${url}?${qs}` : url;
    return this.get<any[]>(fullUrl);
  }
  async archive(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Curriculum.plural}/${(this as any).id}/archive`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async dataForCopy(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Curriculum.plural}/${(this as any).id}/data-for-copy`;
    return BusinessObjectBase.post<any>(url, {});
  }
}
